/*
A component that is given a list of saved views, the selected view
*/

import {
  Box,
  Button,
  Checkbox,
  Chip,
  ComboboxInternalProps,
  ComboboxSelect,
  ComboboxSelectTrigger,
  FormControlLabel,
  Modal,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@chronosphereio/chrono-ui';
import { EventPayload, TimeRangeInputValue } from '@chronosphereio/core';
import { produce } from 'immer';
import { PencilOutline as PencilIcon, TrashCanOutline } from 'mdi-material-ui';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTimeZone } from '@/components';
import { formatTimeRangeInputValue } from '@/components/DateTimeRangePicker/utils';
import { SavedView, VariableDefinition } from '@/core';
import {
  useDefaultViewName,
  useEditMode,
  useTemplateVariableActions,
  useTemplateVariableDefinitions,
  useTemplateVariableViewState,
} from '@/dashboards/context';
import { SAVED_VIEW_EVENTS, SAVED_VIEW_SOURCE, savedViewPayload } from '@/dashboards/utils/saved-view-analytics';
import {
  DEFAULT_VIEW_LABEL,
  DEFAULT_VIEW_VALUE,
  generateDefaultNameFromValues,
  isDuplicateViewName,
  useFindMatchingViews,
} from '@/dashboards/utils/saved-view-utils';
import { useAnalytics, useTimeRange } from '@/plugin-system';
import { useUserTimeFormat } from '@/plugin-system/runtime/useUserTimeFormat';

export type OnUpdateSavedViews = ({
  savedViews,
  defaultViewName,
  updateVariableDefaults,
}: {
  savedViews: SavedView[];
  defaultViewName?: string;
  updateVariableDefaults?: boolean;
}) => void;

export type OnUpdateDefaultValues = ({
  updateTimeRange,
  defaultViewName,
}: {
  updateTimeRange: boolean;
  defaultViewName?: string | null;
}) => void;

interface SavedViewSelectorProps {
  savedViews: SavedView[];
  onUpdateSavedViews?: OnUpdateSavedViews;
  onUpdateDefaultValues?: OnUpdateDefaultValues;
  defaultTimeRange?: TimeRangeInputValue;
  analyticsPayload?: EventPayload;
}

const DEFAULT_SUFFIX = ' (default)';

/**
 * Formats the saved view label based on whether it has been edited.
 * Handles special formatting for default views to avoid double parentheses.
 */
export function formatSavedViewLabel({ label, isEdited }: { label: string | undefined; isEdited: boolean }): string {
  if (!label) {
    return DEFAULT_VIEW_LABEL;
  }

  if (!isEdited) {
    return label;
  }

  if (label.endsWith(DEFAULT_SUFFIX)) {
    return label.replace(DEFAULT_SUFFIX, ' (default - changed)');
  }

  return `${label} (changed)`;
}

// Re-export for consumers that import from this file
export { generateDefaultNameFromValues } from '@/dashboards/utils/saved-view-utils';

// This is an outer wrapper component that is used to provider the specific props
// we need from context.
export const SavedViewSelector = (props: SavedViewSelectorProps) => {
  const { setVariableStateFromSavedView } = useTemplateVariableActions();
  const { currentView, defaultView, isLoading } = useTemplateVariableViewState();
  const defaultViewName = useDefaultViewName();

  return (
    <SavedViewSelectorInternal
      {...props}
      setVariableStateFromSavedView={setVariableStateFromSavedView}
      currentView={currentView}
      defaultView={defaultView}
      defaultViewName={defaultViewName}
      isVariablesLoading={isLoading}
    />
  );
};

const mergeDefaultValues = (view: SavedView, defaultView: SavedView['variable_values']) => {
  return {
    ...view,
    variable_values: {
      ...defaultView,
      ...view.variable_values,
    },
  };
};

type CreateNewViewPayload = { view: SavedView; saveAsDefault: boolean };

export const SavedViewSelectorInternal = ({
  savedViews,
  setVariableStateFromSavedView,
  currentView,
  defaultView,
  defaultViewName,
  onUpdateSavedViews,
  onUpdateDefaultValues,
  defaultTimeRange,
  analyticsPayload,
  isVariablesLoading = false,
}: SavedViewSelectorProps & {
  setVariableStateFromSavedView: (view: SavedView) => void;
  currentView: SavedView['variable_values'];
  defaultView: SavedView['variable_values'];
  defaultViewName?: string;
  isVariablesLoading?: boolean;
}) => {
  const analytics = useAnalytics();
  const matchingViews = useFindMatchingViews(savedViews, currentView, defaultView);
  const firstMatchingView = matchingViews[0];

  // Selector shows 'Default view' while variables are loading
  const [selectedView, setSelectedView] = useState(firstMatchingView?.name ?? DEFAULT_VIEW_VALUE);

  const [editView, setEditView] = useState<string>();
  const [deleteView, setDeleteView] = useState<string>();
  const [previousDefaultViewValues, setPreviousDefaultViewValues] = useState<SavedView['variable_values'] | undefined>(
    undefined
  );

  const { isEditMode } = useEditMode();
  const canMutateSavedViewList = isEditMode || !!onUpdateSavedViews;
  const canEditDefaultViewSettings = isEditMode || !!onUpdateDefaultValues;

  const handleSelectView = useCallback(
    (viewName: string) => {
      setSelectedView(viewName);
      const selectedView = savedViews.find((view) => view.name === viewName);
      if (selectedView) {
        setVariableStateFromSavedView(mergeDefaultValues(selectedView, defaultView));
        analytics?.track(
          SAVED_VIEW_EVENTS.SELECTED,
          savedViewPayload(analyticsPayload, { viewName, isDefault: false, source: SAVED_VIEW_SOURCE.SELECTOR })
        );
      } else if (viewName === DEFAULT_VIEW_VALUE) {
        setVariableStateFromSavedView({ name: 'default', variable_values: defaultView });
        analytics?.track(
          SAVED_VIEW_EVENTS.SELECTED,
          savedViewPayload(analyticsPayload, { viewName, isDefault: true, source: SAVED_VIEW_SOURCE.SELECTOR })
        );
      }
    },
    [setSelectedView, savedViews, setVariableStateFromSavedView, defaultView, analytics, analyticsPayload]
  );

  // When a view is edited, it will no longer be matching.
  // Also account for whether variables are still loading.
  const isViewEdited = selectedView !== undefined && matchingViews.length === 0 && !isVariablesLoading;

  const options = useMemo(() => {
    // Build saved view options (never includes the default)
    const viewOptions = savedViews.map((view) => {
      return {
        label: view.name,
        value: view.name,
      };
    });

    // Saved Views are sorted alphabetically
    // We don't sort the default view as that should be always the first on the list.
    viewOptions.sort((a, b) => a.label.localeCompare(b.label));

    // Build default view option
    const defaultViewOption = {
      value: DEFAULT_VIEW_VALUE,
      // Allow the default view to have an explicit name similar to saved views.
      label: defaultViewName ? `${defaultViewName}${DEFAULT_SUFFIX}` : DEFAULT_VIEW_LABEL,
    };

    // Always show default first, then saved views
    return [defaultViewOption, ...viewOptions];
  }, [savedViews, defaultViewName]);

  const selectedViewOption = options.find((option) => option.value === selectedView);

  const handleSelect = (option: string | null) => {
    if (option !== null) {
      handleSelectView(option);
    }
  };

  const [showCreateModal, setShowCreateModal] = useState(false);

  const handleOpenCreateViewModal = useCallback(() => {
    setShowCreateModal(true);
  }, [setShowCreateModal]);

  const handleCloseCreateViewModal = useCallback(() => {
    setShowCreateModal(false);
  }, [setShowCreateModal]);

  const handleOpenDeleteViewModal = useCallback(
    (viewName: string) => {
      setDeleteView(viewName);
    },
    [setDeleteView]
  );

  const handleCloseDeleteViewModal = useCallback(() => {
    setDeleteView(undefined);
  }, [setDeleteView]);

  const handleOpenEditViewModal = useCallback(
    (viewName: string) => {
      setEditView(viewName);
    },
    [setEditView]
  );

  const handleCloseEditViewModal = useCallback(() => {
    setEditView(undefined);
  }, [setEditView]);

  // This is used for updating the selected views values when the user clicks the "Update View" button.
  const handleUpdateView = useCallback(() => {
    if (selectedView === DEFAULT_VIEW_VALUE) {
      handleOpenEditViewModal(DEFAULT_VIEW_VALUE);
    } else {
      const updatedViews = produce(savedViews, (draft) => {
        const existingViewIndex = draft.findIndex((view) => view.name === selectedView);
        if (existingViewIndex !== -1) {
          draft[existingViewIndex] = {
            ...draft[existingViewIndex],
            name: selectedView,
            variable_values: currentView,
          };
        } else {
          draft.push({ name: selectedView, variable_values: currentView });
        }
      });
      onUpdateSavedViews?.({ savedViews: updatedViews });
      analytics?.track(
        SAVED_VIEW_EVENTS.UPDATED,
        savedViewPayload(analyticsPayload, {
          viewName: selectedView,
          updateType: 'values',
          source: SAVED_VIEW_SOURCE.SELECTOR,
        })
      );
    }
  }, [onUpdateSavedViews, savedViews, selectedView, currentView, handleOpenEditViewModal, analytics, analyticsPayload]);

  const handleCreateNewView = useCallback(
    ({ view, saveAsDefault }: CreateNewViewPayload) => {
      if (saveAsDefault) {
        // When promoting to default, remove it from regular saved views list if it exists.
        // Then update the variable defaults and set the default_view_name so explicit name is preserved.
        const updatedViews = produce(savedViews, (draft) => {
          const existingIndex = draft.findIndex((v) => v.name === view.name);
          if (existingIndex !== -1) {
            draft.splice(existingIndex, 1); // Remove saved view if it exists
          }
        });
        // Ensure current variable state matches the view being promoted to default
        setVariableStateFromSavedView(mergeDefaultValues(view, defaultView));
        onUpdateSavedViews?.({ savedViews: updatedViews, defaultViewName: view.name, updateVariableDefaults: true });
        setSelectedView(DEFAULT_VIEW_VALUE); // Select the default view
        analytics?.track(
          SAVED_VIEW_EVENTS.CREATED,
          savedViewPayload(analyticsPayload, {
            viewName: view.name,
            isDefault: true,
            source: SAVED_VIEW_SOURCE.SELECTOR,
          })
        );
      } else {
        // Add regular (non default) saved views to the list.
        const updatedViews = produce(savedViews, (draft) => {
          draft.push(view);
        });
        onUpdateSavedViews?.({ savedViews: updatedViews });
        setSelectedView(view.name);
        analytics?.track(
          SAVED_VIEW_EVENTS.CREATED,
          savedViewPayload(analyticsPayload, {
            viewName: view.name,
            isDefault: false,
            source: SAVED_VIEW_SOURCE.SELECTOR,
          })
        );
      }
    },
    [
      savedViews,
      onUpdateSavedViews,
      setSelectedView,
      analytics,
      analyticsPayload,
      defaultView,
      setVariableStateFromSavedView,
    ]
  );

  const handleEditView = useCallback(
    (savedView: SavedView, originalView: string) => {
      const updatedViews = produce(savedViews, (draft) => {
        const existingViewIndex = draft.findIndex((view) => view.name === originalView);
        if (existingViewIndex !== -1) {
          draft[existingViewIndex] = savedView; // Update existing view
        } else {
          draft.push(savedView); // Add new view if it doesn't exist
        }
      });
      onUpdateSavedViews?.({ savedViews: updatedViews });
      // If the edited view is currently selected, update selectedView to the new name
      if (selectedView === originalView) {
        setSelectedView(savedView.name);
      }
      analytics?.track(
        SAVED_VIEW_EVENTS.UPDATED,
        savedViewPayload(analyticsPayload, {
          viewName: savedView.name,
          updateType: 'renamed',
          source: SAVED_VIEW_SOURCE.SELECTOR,
        })
      );
    },
    [onUpdateSavedViews, savedViews, analytics, analyticsPayload, selectedView, setSelectedView]
  );

  const handleDeleteView = useCallback(
    (viewName: string) => {
      const updatedViews = produce(savedViews, (draft) => {
        const viewIndex = draft.findIndex((view) => view.name === viewName);
        if (viewIndex !== -1) {
          draft.splice(viewIndex, 1); // Remove the view
        }
      });
      onUpdateSavedViews?.({ savedViews: updatedViews });
      setSelectedView(DEFAULT_VIEW_VALUE);
      analytics?.track(
        SAVED_VIEW_EVENTS.DELETED,
        savedViewPayload(analyticsPayload, { viewName, source: SAVED_VIEW_SOURCE.SELECTOR })
      );
    },
    [onUpdateSavedViews, savedViews, analytics, analyticsPayload]
  );

  const handleUpdateDefaults = useCallback(
    (updateTimeRange: boolean, defaultViewName?: string | null) => {
      onUpdateDefaultValues?.({ updateTimeRange, defaultViewName });
      analytics?.track(
        SAVED_VIEW_EVENTS.UPDATED,
        savedViewPayload(analyticsPayload, { updateType: 'defaults', source: SAVED_VIEW_SOURCE.SELECTOR })
      );
    },
    [onUpdateDefaultValues, analytics, analyticsPayload]
  );

  const handleKeepPreviousView = useCallback(
    (name: string) => {
      if (!previousDefaultViewValues) {
        return;
      }

      // Create a new saved view with the name and previous default view values
      const newView: SavedView = {
        name,
        variable_values: previousDefaultViewValues,
      };

      const updatedViews = produce(savedViews, (draft) => {
        draft.push(newView);
      });

      onUpdateSavedViews?.({ savedViews: updatedViews });
      setPreviousDefaultViewValues(undefined);
      analytics?.track(
        SAVED_VIEW_EVENTS.PREVIOUS_DEFAULT_RESOLVED,
        savedViewPayload(analyticsPayload, { resolution: 'kept', viewName: name, source: SAVED_VIEW_SOURCE.SELECTOR })
      );
    },
    [previousDefaultViewValues, savedViews, onUpdateSavedViews, analytics, analyticsPayload]
  );

  const handleDiscardPreviousView = useCallback(() => {
    setPreviousDefaultViewValues(undefined);
    analytics?.track(
      SAVED_VIEW_EVENTS.PREVIOUS_DEFAULT_RESOLVED,
      savedViewPayload(analyticsPayload, { resolution: 'discarded', source: SAVED_VIEW_SOURCE.SELECTOR })
    );
  }, [analytics, analyticsPayload]);

  const selectedViewLabel = formatSavedViewLabel({
    label: selectedViewOption?.label,
    isEdited: isViewEdited,
  });

  useEffect(() => {
    // If there is at least one matching view, and the selected view is not one of them,
    // we should update the selected view to the first matching view.
    const matchingView = matchingViews.find((view) => view.name === selectedView);
    const firstMatchingView = matchingViews[0];

    if (!matchingView && firstMatchingView !== undefined) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedView(firstMatchingView.name);
    }
  }, [matchingViews, selectedView]);

  const showUpdateAction =
    isViewEdited && (selectedView === DEFAULT_VIEW_VALUE ? canEditDefaultViewSettings : canMutateSavedViewList);
  const showCreateAction = canMutateSavedViewList;
  const hasActions = showUpdateAction || showCreateAction;

  const viewBeingEdited = savedViews.find((view) => view.name === editView);
  const viewBeingDeleted = savedViews.find((view) => view.name === deleteView);

  const { theme } = useTheme();

  // Highlight the selector with a yellow border when in edit mode and values have changed
  const shouldHighlight = isEditMode && isViewEdited;
  const warningColor = shouldHighlight ? theme.palette.status.warning.highContrast : undefined;

  return (
    <>
      <ComboboxSelect
        onReset={() => null}
        renderValue={<ComboboxSelectTrigger value={selectedViewLabel} label="Saved view" minWidth={80} />}
        value={selectedView}
        label="Saved view"
        onSelect={handleSelect}
        options={options}
        showFooter={hasActions}
        borderColor={warningColor}
        secondaryContent={({ highlightedOptionValue, handleClose }) => {
          if (!highlightedOptionValue) {
            return null;
          }
          if (highlightedOptionValue === DEFAULT_VIEW_VALUE) {
            return (
              <SavedViewSecondaryContent
                view={{ name: defaultViewName || DEFAULT_VIEW_VALUE, variable_values: defaultView }}
                onEdit={
                  canEditDefaultViewSettings
                    ? () => {
                        handleOpenEditViewModal(DEFAULT_VIEW_VALUE);
                        handleClose();
                      }
                    : undefined
                }
                defaultTimeRange={defaultTimeRange}
                isDefault={true}
              />
            );
          }

          const view = savedViews.find((v) => v.name === highlightedOptionValue);
          if (view) {
            return (
              <SavedViewSecondaryContent
                view={view}
                onEdit={
                  canMutateSavedViewList
                    ? (viewName: string) => {
                        handleOpenEditViewModal(viewName);
                        handleClose();
                      }
                    : undefined
                }
                onDelete={
                  canMutateSavedViewList
                    ? (viewName: string) => {
                        handleOpenDeleteViewModal(viewName);
                        handleClose();
                      }
                    : undefined
                }
              />
            );
          }
        }}
        renderFooter={({ handleClose }: ComboboxInternalProps) => {
          return (
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <div>
                {showCreateAction && (
                  <Button
                    onClick={() => {
                      handleClose();
                      handleOpenCreateViewModal();
                    }}
                    variant="tertiary"
                  >
                    Create new view
                  </Button>
                )}
              </div>

              <div>
                {showUpdateAction && (
                  <Button
                    onClick={() => {
                      handleUpdateView();
                      handleClose();
                    }}
                    variant="tertiary"
                    data-testid="update-view-button"
                  >
                    Update view
                  </Button>
                )}
              </div>
            </Stack>
          );
        }}
      />
      {showCreateModal && (
        <SavedViewFormModal
          mode="create"
          name=""
          values={currentView}
          onClose={handleCloseCreateViewModal}
          onSubmit={handleCreateNewView}
          savedViews={savedViews}
          defaultView={defaultView}
          defaultViewName={defaultViewName}
          onUpdateDefaultValues={onUpdateDefaultValues}
        />
      )}

      {viewBeingDeleted != undefined && (
        <DeleteViewModal
          view={viewBeingDeleted}
          onClose={handleCloseDeleteViewModal}
          onDelete={() => {
            handleDeleteView(viewBeingDeleted.name);
            handleCloseDeleteViewModal();
          }}
        />
      )}

      {viewBeingEdited !== undefined && (
        <SavedViewFormModal
          mode="edit"
          savedViews={savedViews}
          defaultView={defaultView}
          defaultViewName={defaultViewName}
          onUpdateDefaultValues={onUpdateDefaultValues}
          name={viewBeingEdited.name}
          values={viewBeingEdited.variable_values}
          onClose={handleCloseEditViewModal}
          onSubmit={({ view, saveAsDefault }) => {
            if (saveAsDefault) {
              // Store the current default view before promoting
              // If it has a name, save it as a new SavedView; otherwise store in state
              const updatedViews = produce(savedViews, (draft) => {
                // If the current default view has a name, add it as a saved view
                if (defaultViewName) {
                  draft.push({
                    name: defaultViewName,
                    variable_values: defaultView,
                  });
                }
                // Remove the view being promoted from saved views
                const viewIndex = draft.findIndex((v) => v.name === viewBeingEdited.name);
                if (viewIndex !== -1) {
                  draft.splice(viewIndex, 1); // Remove the original view
                }
              });
              // If the current default view doesn't have a name, store it in state
              if (!defaultViewName) {
                setPreviousDefaultViewValues(defaultView);
              }
              // Ensure current variable state matches the view being promoted
              setVariableStateFromSavedView(mergeDefaultValues(view, defaultView));
              // Make it atomic - update saved views, default_view_name, and variable defaults in one call
              onUpdateSavedViews?.({
                savedViews: updatedViews,
                defaultViewName: view.name,
                updateVariableDefaults: true,
              });
              setSelectedView(DEFAULT_VIEW_VALUE);
            } else {
              // Regular edit - just update the saved view
              handleEditView(view, viewBeingEdited.name);
            }
            handleCloseEditViewModal();
          }}
        />
      )}

      {editView === DEFAULT_VIEW_VALUE && (
        <DefaultViewFormModal
          values={selectedView === DEFAULT_VIEW_VALUE ? currentView : defaultView}
          onClose={handleCloseEditViewModal}
          onSubmit={handleUpdateDefaults}
          defaultViewName={defaultViewName}
          savedViews={savedViews}
        />
      )}

      {previousDefaultViewValues !== undefined && (
        <PreviousDefaultViewDialog
          values={previousDefaultViewValues}
          savedViews={savedViews}
          defaultView={defaultView}
          defaultViewName={defaultViewName}
          onKeep={handleKeepPreviousView}
          onDiscard={handleDiscardPreviousView}
        />
      )}
    </>
  );
};

const SavedViewSecondaryContent = ({
  view,
  onEdit,
  onDelete,
  defaultTimeRange,
  isDefault = false,
}: {
  view: SavedView;
  onEdit?: (viewName: string) => void;
  onDelete?: (viewName: string) => void;
  defaultTimeRange?: TimeRangeInputValue;
  isDefault?: boolean;
}) => {
  const timerangeLabel = useTimeRangeLabel(defaultTimeRange);
  const variableDefinitions = useTemplateVariableDefinitions();

  return (
    <Box
      sx={{
        width: 500,
        maxHeight: 500,
        borderLeft: '1px solid',
        borderColor: 'border.default',
        padding: 1,
        textOverflow: 'ellipsis',
      }}
    >
      <Box height="100%">
        <Stack height={'100%'} direction={'column'}>
          <Box my={1}>
            <Typography variant="h3">
              {(() => {
                if (isDefault && view.name !== DEFAULT_VIEW_VALUE) {
                  return `${view.name}${DEFAULT_SUFFIX}`;
                }
                if (view.name === DEFAULT_VIEW_VALUE) {
                  return DEFAULT_VIEW_LABEL;
                }
                return view.name;
              })()}
            </Typography>
            {timerangeLabel && isDefault && (
              <Typography variant="body2" color={'text.secondary'}>
                {timerangeLabel}
              </Typography>
            )}
          </Box>

          <div>
            <Typography variant="body2" fontWeight="bold" color={'text.secondary'}>
              Variables
            </Typography>
            <ListValues values={view.variable_values} variableDefinitions={variableDefinitions} />
          </div>

          <Box alignContent="flex-end" flexGrow={1}>
            {((!isDefault && onDelete) || onEdit) && (
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <div>
                  {!isDefault && onDelete && (
                    <Button
                      onClick={() => {
                        onDelete(view.name);
                      }}
                      variant="tertiary"
                      StartIcon={<TrashCanOutline />}
                    >
                      Delete
                    </Button>
                  )}
                </div>

                <div>
                  {onEdit && (
                    <Button
                      onClick={() => {
                        onEdit(view.name);
                      }}
                      variant="tertiary"
                      StartIcon={<PencilIcon />}
                    >
                      Edit
                    </Button>
                  )}
                </div>
              </Stack>
            )}
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

// List the values of the saved view in Chips
const ListValues = ({
  values,
  variableDefinitions,
  excludedVariables,
  onRemoveStale,
  enableRemoval = false,
}: {
  values: SavedView['variable_values'];
  variableDefinitions?: VariableDefinition[];
  excludedVariables?: Set<string>;
  onRemoveStale?: (variableName: string) => void;
  enableRemoval?: boolean;
}) => {
  const variableNames = variableDefinitions?.map((v) => v.spec.name) ?? [];

  return (
    <>
      {Object.entries(values).map(([key, value], idx) => {
        const isStale = variableDefinitions && !variableNames.includes(key);
        const isExcluded = excludedVariables?.has(key);

        const canRemove = isStale && enableRemoval && onRemoveStale;

        if (isExcluded) {
          return null;
        }

        return (
          <Fragment key={idx}>
            <Chip
              label={
                <span>
                  {key}=
                  <Box component="span" sx={{ fontWeight: (theme) => theme.typography.fontWeightBold }}>
                    {Array.isArray(value) ? value.join(', ') : value}
                  </Box>
                </span>
              }
              sx={{
                paddingRight: canRemove ? 0.5 : 1,
                opacity: isStale ? 0.5 : 1,
                backgroundColor: isStale ? 'action.disabledBackground' : undefined,
                textDecoration: isStale ? 'line-through' : undefined,
              }}
              title={isStale ? 'This variable no longer exists' : undefined}
              onDelete={
                canRemove
                  ? () => {
                      onRemoveStale(key);
                    }
                  : undefined
              }
            />{' '}
          </Fragment>
        );
      })}
    </>
  );
};

const SavedViewFormModal = ({
  values,
  onClose,
  onSubmit,
  mode,
  name,
  savedViews,
  defaultView,
  defaultViewName,
  onUpdateDefaultValues,
}: {
  values: SavedView['variable_values'];
  name: string;
  onClose: () => void;
  onSubmit: ({ view, saveAsDefault }: CreateNewViewPayload) => void;
  mode: 'create' | 'edit';
  savedViews: SavedView[];
  defaultView: SavedView['variable_values'];
  defaultViewName?: string;
  onUpdateDefaultValues?: ({ updateTimeRange }: { updateTimeRange: boolean }) => void;
}) => {
  const defaultName = generateDefaultNameFromValues(values);
  const [localName, setLocalName] = useState(name || defaultName);
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  const [excludedVariables, setExcludedVariables] = useState<Set<string>>(new Set());
  const variableDefinitions = useTemplateVariableDefinitions();

  const staleVariables = useMemo(() => {
    if (!variableDefinitions) {
      return [];
    }
    const variableNames = variableDefinitions.map((v) => v.spec.name);
    return Object.keys(values).filter((key) => !variableNames.includes(key));
  }, [values, variableDefinitions]);

  // Detect if the current values match any existing saved view
  const matchingViews = useFindMatchingViews(savedViews, values, defaultView);
  // Filter out the current view being edited to avoid showing "this view matches itself"
  const otherMatchingViews = mode === 'edit' ? matchingViews.filter((view) => view.name !== name) : matchingViews;
  const hasMatchingView = otherMatchingViews.length > 0;
  // Format all matching view names for display
  const matchingViewNames = otherMatchingViews
    .map((view) => (view.name === DEFAULT_VIEW_VALUE ? DEFAULT_VIEW_LABEL : view.name))
    .join(', ');

  // Validate duplicate name
  const isDuplicateName = useMemo(
    () => isDuplicateViewName(localName, savedViews, defaultViewName, mode === 'edit' ? name : undefined),
    [localName, savedViews, defaultViewName, mode, name]
  );

  const { isEditMode } = useEditMode();

  return (
    <Modal
      open={true}
      title={`${mode === 'create' ? 'Create' : 'Edit'} saved view`}
      onClose={onClose}
      onPrimaryButtonClick={() => {
        // Filter out excluded variables
        const filteredValues = Object.entries(values).reduce(
          (acc, [key, value]) => {
            if (!excludedVariables.has(key)) {
              acc[key] = value;
            }
            return acc;
          },
          {} as SavedView['variable_values']
        );
        onSubmit({ view: { name: localName, variable_values: filteredValues }, saveAsDefault });
        onClose();
      }}
      primaryButtonText={isEditMode ? 'Apply' : 'Save'}
      primaryButtonDisabled={localName === '' || isDuplicateName}
    >
      <Box mt={2} width={'50%'}>
        <TextField
          fullWidth
          label="Name"
          value={localName}
          onChange={(v) => {
            setLocalName(v.target.value);
          }}
          error={isDuplicateName}
          helperText={isDuplicateName ? 'This name is already in use by another view' : undefined}
        />
      </Box>
      <Box my={2}>
        <Typography variant="body2" color={'text.secondary'} fontWeight={'bold'}>
          Variables
        </Typography>
        {staleVariables.length > excludedVariables.size && (
          <Typography variant="body2" color="text.secondary" mb={1}>
            Some variables no longer exist and can be removed.
          </Typography>
        )}
        <ListValues
          values={values}
          variableDefinitions={variableDefinitions}
          excludedVariables={excludedVariables}
          onRemoveStale={(variableName) => {
            setExcludedVariables((prev) => new Set(prev).add(variableName));
          }}
          enableRemoval={true}
        />
      </Box>
      {hasMatchingView && (
        <Box my={1}>
          <Typography variant="body2" color="text.secondary">
            The same set of variable values {otherMatchingViews.length === 1 ? 'is' : 'are'} currently used in:{' '}
            {matchingViewNames}
          </Typography>
        </Box>
      )}
      {onUpdateDefaultValues && (
        <FormControlLabel
          control={<Checkbox checked={saveAsDefault} onChange={(_, checked) => setSaveAsDefault(checked)} />}
          label={isEditMode ? 'Apply as default view' : 'Save as default view'}
        />
      )}
    </Modal>
  );
};

const useTimeRangeLabel = (timeRange?: TimeRangeInputValue) => {
  const { timeZone } = useTimeZone();
  const timeFormat = useUserTimeFormat();

  if (!timeRange) {
    return undefined;
  }

  return formatTimeRangeInputValue({ timeRange, timeZone, timeFormat });
};

const DefaultViewFormModal = ({
  values,
  onClose,
  onSubmit,
  defaultViewName,
  savedViews,
}: {
  values: SavedView['variable_values'];
  onClose: () => void;
  onSubmit: (updateTimeRange: boolean, defaultViewName?: string | null) => void;
  defaultViewName?: string;
  savedViews: SavedView[];
}) => {
  const defaultName = generateDefaultNameFromValues(values);
  const [updateDefaultTimeRange, setUpdateDefaultTimeRange] = useState(true);
  const [localDefaultViewName, setLocalDefaultViewName] = useState(defaultViewName || defaultName);
  const variableDefinitions = useTemplateVariableDefinitions();

  // Validate duplicate name
  const isDuplicateName = useMemo(() => {
    if (!localDefaultViewName.trim()) return false;

    return savedViews.some((v) => v.name === localDefaultViewName.trim());
  }, [localDefaultViewName, savedViews]);

  const { timeRange } = useTimeRange();
  const timerangeLabel = useTimeRangeLabel(timeRange);

  const { isEditMode } = useEditMode();

  return (
    <Modal
      open={true}
      title={`Edit default view`}
      onClose={onClose}
      onPrimaryButtonClick={() => {
        // Pass the edited default_view_name (required, so always non-empty)
        onSubmit(updateDefaultTimeRange, localDefaultViewName);
        onClose();
      }}
      primaryButtonText={isEditMode ? 'Apply' : 'Save'}
      primaryButtonDisabled={isDuplicateName || !localDefaultViewName.trim()}
    >
      <Box mt={2} width={'50%'}>
        <TextField
          fullWidth
          label="Name"
          value={localDefaultViewName}
          onChange={(v) => {
            setLocalDefaultViewName(v.target.value);
          }}
          error={isDuplicateName}
          helperText={isDuplicateName ? 'This name is already in use by another view' : undefined}
        />
      </Box>

      <Box my={2}>
        <Typography variant="body2" color={'text.secondary'} fontWeight={'bold'}>
          Variables
        </Typography>
        <ListValues values={values} variableDefinitions={variableDefinitions} />
      </Box>

      <div>
        <FormControlLabel
          control={
            <Checkbox checked={updateDefaultTimeRange} onChange={(_, checked) => setUpdateDefaultTimeRange(checked)} />
          }
          label={`${isEditMode ? 'Apply' : 'Save'} current time range as default`}
        />

        <Typography variant="body2" color="text.secondary" ml={4}>
          {timerangeLabel}
        </Typography>
      </div>
    </Modal>
  );
};

const DeleteViewModal = ({
  view,
  onClose,
  onDelete,
}: {
  view: SavedView;
  onClose: () => void;
  onDelete: () => void;
}) => {
  const variableDefinitions = useTemplateVariableDefinitions();
  return (
    <Modal
      open={true}
      title="Delete Saved View"
      onClose={onClose}
      onPrimaryButtonClick={() => {
        onDelete();
        onClose();
      }}
      primaryButtonText="Delete"
    >
      <Typography>
        Are you sure you want to delete the saved view <strong>{view.name}</strong>?
      </Typography>
      <Box mt={2}>
        <Typography variant="body2" color={'text.secondary'}>
          Variables
        </Typography>
        <ListValues values={view.variable_values} variableDefinitions={variableDefinitions} />
      </Box>
    </Modal>
  );
};

const PreviousDefaultViewDialog = ({
  values,
  savedViews,
  defaultView,
  onKeep,
  onDiscard,
  defaultViewName,
}: {
  values: SavedView['variable_values'];
  savedViews: SavedView[];
  defaultView: SavedView['variable_values'];
  onKeep: (name: string) => void;
  onDiscard: () => void;
  defaultViewName?: string;
}) => {
  const defaultName = generateDefaultNameFromValues(values);
  const [name, setName] = useState<string>('');
  const variableDefinitions = useTemplateVariableDefinitions();

  // Detect if the current values match any existing saved view
  const matchingViews = useFindMatchingViews(savedViews, values, defaultView);
  // Filter out the current view being edited to avoid showing "this view matches itself"
  const hasMatchingView = matchingViews.length > 0;
  // Format all matching view names for display
  const matchingViewNames = matchingViews
    .map((view) => (view.name === DEFAULT_VIEW_VALUE ? DEFAULT_VIEW_LABEL : view.name))
    .join(', ');

  // Validate duplicate name
  const currentName = name || defaultName;
  const isDuplicateName = useMemo(
    () => isDuplicateViewName(currentName, savedViews, defaultViewName),
    [currentName, savedViews, defaultViewName]
  );

  const handleDiscard = () => {
    onDiscard();
    setName('');
  };

  const handleKeep = () => {
    // Default to keep behavior when dialog is closed
    onKeep(name || defaultName);
    setName('');
  };

  return (
    <Modal
      open={true}
      title="Store current default view as saved view"
      onClose={handleDiscard}
      onPrimaryButtonClick={handleKeep}
      primaryButtonText="Save"
      primaryButtonDisabled={isDuplicateName}
      secondaryButtonText="Discard"
    >
      <Box my={2}>
        <Stack gap={1} marginBottom={2}>
          Save current default view values as a new saved view with this name:
          <TextField
            fullWidth
            label="Name"
            value={name || defaultName}
            onChange={(v) => {
              setName(v.target.value);
            }}
            error={isDuplicateName}
            helperText={isDuplicateName ? 'This name is already in use by another view' : undefined}
          />
        </Stack>

        <Typography variant="body2" color={'text.secondary'} fontWeight={'bold'}>
          Variables
        </Typography>
        <ListValues values={values} variableDefinitions={variableDefinitions} />

        {hasMatchingView && (
          <Box my={1}>
            <Typography variant="body2" color="text.secondary">
              The same set of variable values {matchingViews.length === 1 ? 'is' : 'are'} currently used in:{' '}
              {matchingViewNames}
            </Typography>
          </Box>
        )}
      </Box>
    </Modal>
  );
};
